import { Check, ChevronRight } from 'lucide-react';
import { Badge } from '@vnetwork/ui/primitives';
import { JOURNEY_STEP_ORDER, type CustomerJourney, type JourneyStep } from '@/api/customer-journey';
import { railTone, stepLabel, stepIndex, isLostStep, type Tone } from './sale-process-helpers';

/**
 * Horizontal pipeline rail for a single journey. Renders `JOURNEY_STEP_ORDER`
 * as tone-colored step markers (done=success ✓, current=info, future=neutral;
 * a lost journey paints not-yet-done steps danger). Read-only — advancing the
 * step is driven by the journey actions, not by clicking the rail.
 */

/** Marker background/foreground per tone, using intent tokens (dark-safe). */
const TONE_BG: Record<Tone, string> = {
  success: 'var(--vn-success-solid)',
  info: 'var(--vn-info-solid)',
  danger: 'var(--vn-danger-solid)',
  warning: 'var(--vn-warning-solid)',
  neutral: 'var(--vn-bg-muted)',
};

const TONE_FG: Record<Tone, string> = {
  success: 'var(--vn-fg-on-solid)',
  info: 'var(--vn-fg-on-solid)',
  danger: 'var(--vn-fg-on-solid)',
  warning: 'var(--vn-fg-on-solid)',
  neutral: 'var(--vn-fg-muted)',
};

function StepMarker({ tone, done, index }: { tone: Tone; done: boolean; index: number }) {
  return (
    <span
      aria-hidden
      style={{
        width: 24,
        height: 24,
        borderRadius: '50%',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        background: TONE_BG[tone],
        color: TONE_FG[tone],
        fontSize: 'var(--vn-font-size-xs)',
        fontWeight: 'var(--vn-font-weight-bold)',
        border: tone === 'neutral' ? '1px solid var(--vn-border-default)' : 'none',
      }}
    >
      {done ? <Check size={14} strokeWidth={3} /> : index + 1}
    </span>
  );
}

export function JourneyPipelineStepper({ journey }: { journey: CustomerJourney }) {
  const activeStep = journey.customer_journey_step;
  const active = stepIndex(activeStep);
  const lost = isLostStep(activeStep);

  return (
    <div
      role="list"
      aria-label="Journey pipeline"
      style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 'var(--vn-space-2)' }}
    >
      {JOURNEY_STEP_ORDER.map((step: JourneyStep, i) => {
        const tone = railTone(step, activeStep);
        const idx = stepIndex(step);
        const done = !lost && active !== -1 && idx < active;
        const isCurrent = idx === active;
        return (
          <div key={step} role="listitem" style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
            {i > 0 ? <ChevronRight size={14} style={{ color: 'var(--vn-fg-subtle)', flexShrink: 0 }} /> : null}
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
              <StepMarker tone={tone} done={done} index={idx} />
              <span
                style={{
                  fontSize: 'var(--vn-font-size-sm)',
                  fontWeight: isCurrent ? 'var(--vn-font-weight-semibold)' : 'var(--vn-font-weight-medium)',
                  color: isCurrent ? 'var(--vn-fg-default)' : 'var(--vn-fg-muted)',
                  whiteSpace: 'nowrap',
                }}
              >
                {stepLabel(step)}
              </span>
            </div>
          </div>
        );
      })}

      {lost ? (
        <Badge tone="danger" appearance="subtle" size="sm">
          {stepLabel(activeStep)}
        </Badge>
      ) : null}
    </div>
  );
}

export default JourneyPipelineStepper;
